import type { UseFormRegister } from "react-hook-form";
import { Lock } from "lucide-react";

import BookerSelect from "../../../components/BookerSelect";
import CurrencySelect from "../../../components/CurrencySelect";
import { FormCheckbox, FormCheckboxGroup } from "@/components/forms/FormCheckbox";
import { FormFieldShell } from "@/components/forms/FormField";
import { FormInput } from "@/components/forms/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/forms/FormRadio";
import { Heading } from "@/components/typography";
import { Card, CardContent } from "@/components/ui/card";
import { FieldGroup } from "@/components/ui/field";
import type { CampaignType, Client, Currency, Platform, User } from "../../../shared/types";
import type { CampaignFormValues } from "../campaign";

const OPPORTUNITY_PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

type CampaignBasicsSectionProps = {
    isManager: boolean;
    booker: User | undefined;
    bookers: User[];
    clients: Client[];
    bookerLocked: boolean;
    basicsLocked: boolean;
    basicsErrors: { booker?: string; platforms?: string; currency?: string };
    onBookerSelect: (next?: User) => void;
    currency?: Currency;
    currencyLockedFromBooker: boolean;
    onCurrencyChange: (currency: Currency) => void;
    campaignNameError?: string;
    register: UseFormRegister<CampaignFormValues>;
    platforms: Platform[];
    onTogglePlatform: (platform: Platform, checked: boolean) => void;
    campaignType: CampaignType;
    onCampaignTypeChange: (type: CampaignType) => void;
};

/** Booker, currency, name, platforms, and campaign type. Booker locks after publish; the rest lock once a pitch exists. */
export function CampaignBasicsSection({
    isManager,
    booker,
    bookers,
    clients,
    bookerLocked,
    basicsLocked,
    basicsErrors,
    onBookerSelect,
    currency,
    currencyLockedFromBooker,
    onCurrencyChange,
    campaignNameError,
    register,
    platforms,
    onTogglePlatform,
    campaignType,
    onCampaignTypeChange,
}: CampaignBasicsSectionProps) {
    return (
        <section id="campaign-basics" className="scroll-mt-16">
            {/* Title + optional lock sit on one row; margins cannot place the icon. */}
            <div className="mb-4 flex items-center gap-2">
                <Heading type="h4" className="font-bold">
                    Campaign Basics
                </Heading>
                {basicsLocked ? (
                    <Lock className="size-4 text-muted-foreground" aria-label="Locked" />
                ) : null}
            </div>
            <Card size="sm">
                <CardContent>
                    <FieldGroup className="gap-6">
                        {isManager ? (
                            <FormFieldShell
                                label="Booker"
                                required
                                htmlFor="booker-select"
                                invalid={Boolean(basicsErrors.booker)}
                                error={basicsErrors.booker}
                                disabled={bookerLocked}
                            >
                                {() => (
                                    <BookerSelect
                                        value={booker}
                                        bookers={bookers}
                                        clients={clients}
                                        disabled={bookerLocked}
                                        onBookerSelect={onBookerSelect}
                                    />
                                )}
                            </FormFieldShell>
                        ) : (
                            <FormInput
                                label="Booker"
                                value={booker?.email || ""}
                                disabled
                                readOnly
                            />
                        )}

                        <FormFieldShell
                            label="Currency"
                            required
                            htmlFor="currency-select"
                            invalid={Boolean(basicsErrors.currency)}
                            error={basicsErrors.currency}
                            disabled={basicsLocked || currencyLockedFromBooker}
                        >
                            {() => (
                                <CurrencySelect
                                    hideLabel
                                    className="w-full"
                                    triggerClassName="my-0 h-9 w-full min-w-0"
                                    value={currency ?? null}
                                    onChange={(next) => {
                                        if (next) {
                                            onCurrencyChange(next as Currency);
                                        }
                                    }}
                                    locked={currencyLockedFromBooker}
                                    disabled={basicsLocked}
                                    disabledReason="Campaign basics lock after the first pitch."
                                />
                            )}
                        </FormFieldShell>

                        <FormInput
                            label="Campaign Name"
                            required
                            placeholder="My Summer Campaign"
                            disabled={basicsLocked}
                            error={campaignNameError}
                            {...register("campaignName", { required: true })}
                        />

                        <FormCheckboxGroup
                            label="Platforms"
                            required
                            disabled={basicsLocked}
                            invalid={Boolean(basicsErrors.platforms)}
                            error={basicsErrors.platforms}
                            fieldClassName={basicsLocked ? "opacity-60" : undefined}
                        >
                            {OPPORTUNITY_PLATFORMS.map((platform) => (
                                <FormCheckbox
                                    key={platform}
                                    label={platform}
                                    checked={platforms.includes(platform)}
                                    disabled={basicsLocked}
                                    onChange={(checked) => onTogglePlatform(platform, checked)}
                                />
                            ))}
                        </FormCheckboxGroup>

                        <FormRadioGroup
                            label="Campaign Type"
                            required
                            value={campaignType}
                            onChange={(next) => {
                                if (next === "Song" || next === "Brand") {
                                    onCampaignTypeChange(next);
                                }
                            }}
                            disabled={basicsLocked}
                            fieldClassName={basicsLocked ? "opacity-60" : undefined}
                        >
                            <FormRadio value="Song" label="Song" disabled={basicsLocked} />
                            <FormRadio value="Brand" label="Brand" disabled={basicsLocked} />
                        </FormRadioGroup>
                    </FieldGroup>
                </CardContent>
            </Card>
        </section>
    );
}
